import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,  Star, MapPin, Award, 
  CheckCircle, AlertCircle 
} from 'lucide-react';
import apiService from '../../services/api';
import ConsultationForm from '../../components/forms/ConsultationForm';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/ui/Alert';
import Button from '../../components/ui/Button';

const BookConsultation = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    if (doctorId) {
      loadDoctor();
    }
  }, [doctorId]);

  const loadDoctor = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.doctors.getById(doctorId);
      
      if (response.success) {
        setDoctor(response.data);
      } else {
        setError('Doctor not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load doctor details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError('');

      // Prepare consultation data
      const consultationData = {
        // Patient details for account creation
        patientDetails: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        
        // Consultation details
        doctorId: doctorId,
        type: formData.type,
        symptoms: formData.symptoms,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        patientNotes: formData.patientNotes,
        
        // Medical information
        medicalInfo: {
          allergies: formData.allergies,
          currentMedications: formData.currentMedications,
          chronicConditions: formData.chronicConditions,
        },
        
        // Emergency contact
        emergencyContact: formData.emergencyContactName ? {
          name: formData.emergencyContactName,
          phone: formData.emergencyContactPhone,
        } : null,
        
        // Attachments
        attachments: formData.attachments || []
      };

      // Call the public booking endpoint
      const response = await apiService.post(
        '/consultations/public/book',
        consultationData
      );

      if (response.success) {
        setSuccess(true);
        setBookingDetails(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to book consultation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !doctor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Doctor Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/doctors')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Doctor Directory
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Booking Successful!
            </h2>
            <p className="text-gray-600">
              Your consultation has been booked successfully
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3">
              What happens next?
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  We've sent a confirmation email to <strong>{bookingDetails?.email}</strong>
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  Dr. {doctor.firstName} {doctor.lastName} will review your request and confirm the appointment
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  You'll receive a notification once the doctor accepts your booking
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  An account has been created for you. Check your email to set your password and access your consultation history
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Booking Reference</h4>
            <p className="text-2xl font-mono font-bold text-primary-600 mb-2">
              {bookingDetails?.bookingReference || 'N/A'}
            </p>
            <p className="text-sm text-gray-600">
              Keep this reference number for your records
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/doctors')}
              className="flex-1"
            >
              Browse More Doctors
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="flex-1"
            >
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        <div className="max-w-4xl mx-auto">
          {/* Doctor Info Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-start gap-6">
              {/* Doctor Avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary-600">
                    {doctor.firstName?.charAt(0)}{doctor.lastName?.charAt(0)}
                  </span>
                </div>
              </div>

              {/* Doctor Details */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-2xl font-bold text-gray-900">
                        Dr. {doctor.firstName} {doctor.lastName}
                      </h2>
                      {doctor.isVerified && (
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    <p className="text-primary-600 font-medium text-lg">
                      {doctor.specialization}
                    </p>
                  </div>
                  
                  {doctor.rating?.average > 0 && (
                    <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{doctor.rating.average.toFixed(1)}</span>
                      <span className="text-sm text-gray-600">({doctor.rating.count})</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                  {doctor.experience > 0 && (
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      <span>{doctor.experience} years experience</span>
                    </div>
                  )}
                  {doctor.hospital?.name && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{doctor.hospital.name}</span>
                    </div>
                  )}
                </div>

                {doctor.about && (
                  <p className="text-gray-600 text-sm">
                    {doctor.about}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert type="error" title="Error" onClose={() => setError('')} className="mb-6">
              {error}
            </Alert>
          )}

          {/* Booking Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Book Your Consultation
            </h2>
            
            <ConsultationForm
              doctor={doctor}
              onSubmit={handleSubmit}
              loading={submitting}
              requiresAuth={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultation;